import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import type {
  RealSpreadsheetId, SpreadsheetId,
} from '~/_shared/types/spreadsheetData/spreadsheetRow';
import {
  createUuid, type Uuid,
} from '~/_shared/utils/createUuid';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import { isString } from '~/_shared/utils/typeGuards';
import type { RealSpreadsheetVersionId } from '~/spreadsheet/spreadsheet.repository';
import {
  type ChartMetric, ChartMetricType,
  isChartMetricLocationCount,
  isChartMetricSpreadsheetColumn,
} from '../mapSettings/data/charts/mapSettingsCharts.types';
import type { BoundaryStatsResponseItem } from './stats.repository';
import type {
  BoundaryStatItemDemographic, BoundaryStatItemLocationCount, BoundaryStatItemLocationCountMetricData,
  BoundaryStatItemSpreadsheetColumn, BoundaryStatItemSpreadsheetColumnMetricData, BoundaryStatsItem, StatItem,
} from './stats.state';

export const createEmptyBoundaryStatItemSpreadsheetColumn = ({
  boundaryGroupId,
  column,
}: {
  boundaryGroupId: BoundaryStatItemSpreadsheetColumnMetricData['boundaryGroupId'];
  column: BoundaryStatItemSpreadsheetColumnMetricData['column'];
}): BoundaryStatItemSpreadsheetColumn => ({
  boundaryGroupId,
  column,
  id: createUuid(),
  type: ChartMetricType.SpreadsheetColumn,
  data: {
    status: FetchableResourceStatus.Requested,
  },
});

export const createEmptyBoundaryStatItemLocationCount = ({
  boundaryGroupId, column, columnValue, spreadsheetId,
}: {
  boundaryGroupId: BoundaryStatItemLocationCountMetricData['boundaryGroupId'];
  column?: BoundaryStatItemLocationCountMetricData['column'];
  columnValue?: string;
  spreadsheetId?: SpreadsheetId;
}): BoundaryStatItemLocationCount => ({
  id: createUuid(),
  boundaryGroupId,
  column,
  type: ChartMetricType.LocationCount,
  columnValue,
  data: {
    status: FetchableResourceStatus.Requested,
  },
  spreadsheetId: column ? undefined : spreadsheetId,
});

export const createEmptyBoundaryStatItemDemographic = ({
  boundaryGroupId,
  demographicId,
}: {
  boundaryGroupId: BoundaryStatItemDemographic['boundaryGroupId'];
  demographicId: BoundaryStatItemDemographic['demographicId'];
}): BoundaryStatItemDemographic => ({
  id: createUuid(),
  boundaryGroupId,
  demographicId,
  type: ChartMetricType.Demographic,
  data: {
    status: FetchableResourceStatus.Requested,
  },
});

export const createEmptyBoundaryStatItem = ({
  boundaryGroupId,
  chartMetric,
}: {
  boundaryGroupId: BoundaryStatItemDemographic['boundaryGroupId'];
  chartMetric: ChartMetric;
}): BoundaryStatsItem => {
  if (isChartMetricSpreadsheetColumn(chartMetric)) {
    return createEmptyBoundaryStatItemSpreadsheetColumn({
      boundaryGroupId,
      column: chartMetric.column,
    });
  }
  if (isChartMetricLocationCount(chartMetric)) {
    return createEmptyBoundaryStatItemLocationCount({
      boundaryGroupId,
      column: chartMetric.column,
      spreadsheetId: chartMetric.spreadsheetId,
      columnValue: chartMetric.columnValue,
    });
  }
  return createEmptyBoundaryStatItemDemographic({
    boundaryGroupId,
    demographicId: chartMetric.demographicId,
  });
};

export const createLoadedStatItem = (
  id: Uuid,
  serverItem: BoundaryStatsResponseItem,
): StatItem | null => {
  if (serverItem.type === ChartMetricType.SpreadsheetColumn) {
    if (!serverItem.spreadsheet_version || !serverItem.spreadsheet_id || !isString(serverItem.metric)) {
      return null;
    }

    return {
      id,
      boundaryGroupId: serverItem.boundary_group_id,
      column: { spreadsheetId: serverItem.spreadsheet_id, columnId: serverItem.metric },
      type: ChartMetricType.SpreadsheetColumn,
      data: {
        status: FetchableResourceStatus.Loaded,
        value: {
          serverId: serverItem.id,
          computedAt: new Date(serverItem.computed_at),
          ...(serverItem.data ? {
            data: {
              median: serverItem.data.median,
              avg: serverItem.data.avg,
              values: serverItem.data.values || {},
            },
          } : {}),
          extra: {
            boundaryGroupEtag: serverItem.boundary_group_timestamp,
            spreadsheetVersions: stringObjectEntries(serverItem.spreadsheet_version).reduce((acc, [realSpreadsheetIdString, versionId]) => {
              acc.set(+realSpreadsheetIdString, versionId);
              return acc;
            }, new Map<RealSpreadsheetId, RealSpreadsheetVersionId>()),
          },
          status: serverItem.status,
        },
      },
    };
  }

  if (serverItem.type === ChartMetricType.LocationCount) {
    if (!serverItem.spreadsheet_version || !serverItem.spreadsheet_id) {
      return null;
    }

    const column = serverItem.metric
      ? { spreadsheetId: serverItem.spreadsheet_id, columnId: serverItem.metric }
      : undefined;

    return {
      id,
      boundaryGroupId: serverItem.boundary_group_id,
      column,
      type: ChartMetricType.LocationCount,
      columnValue: serverItem.column_value ?? undefined,
      spreadsheetId: serverItem.spreadsheet_id,
      data: {
        status: FetchableResourceStatus.Loaded,
        value: {
          serverId: serverItem.id,
          computedAt: new Date(serverItem.computed_at),
          ...(serverItem.data ? {
            data: {
              median: serverItem.data.median,
              avg: serverItem.data.avg,
              values: serverItem.data.values || {},
            },
          } : {}),
          extra: {
            boundaryGroupEtag: serverItem.boundary_group_timestamp,
            spreadsheetVersions: stringObjectEntries(serverItem.spreadsheet_version).reduce((acc, [realSpreadsheetIdString, versionId]) => {
              acc.set(+realSpreadsheetIdString, versionId);
              return acc;
            }, new Map<RealSpreadsheetId, RealSpreadsheetVersionId>()),
          },
          status: serverItem.status,
        },
      },
    };
  }

  if (serverItem.type === ChartMetricType.Demographic) {

    return {
      id,
      boundaryGroupId: serverItem.boundary_group_id,
      demographicId: serverItem.metric,
      type: ChartMetricType.Demographic,
      data: {
        status: FetchableResourceStatus.Loaded,
        value: {
          serverId: serverItem.id,
          computedAt: new Date(serverItem.computed_at),
          ...(serverItem.data ? {
            data: {
              median: serverItem.data.median,
              avg: serverItem.data.avg,
              values: serverItem.data.values || {},
            },
          } : {}),
          extra: {
            boundaryGroupEtag: serverItem.boundary_group_timestamp,
          },
          status: serverItem.status,
        },
      },
    };
  }

  return null;
};
